/**
 * Observability Service
 * Handles centralized logging, performance metrics, and system diagnostics.
 * Integrates with Sentry for production error tracking.
 */

import * as Sentry from '@sentry/react';

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: unknown;
}

export interface SystemHealth {
  storage: 'healthy' | 'warning' | 'error';
  network: 'online' | 'offline';
  aiEngine: 'ready' | 'missing-key' | 'unreachable';
  permissions: 'granted' | 'denied' | 'prompt';
  latency: number;
}

interface PerformanceMetric {
  name: string;
  startTime: number;
  duration?: number;
}

class Logger {
  private history: LogEntry[] = [];
  private maxHistory = 100;
  private performanceMetrics: Map<string, PerformanceMetric> = new Map();

  private log(level: LogLevel, message: string, context?: unknown) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
    };

    this.history.unshift(entry);
    if (this.history.length > this.maxHistory) this.history.pop();

    const styles = {
      info: 'color: #0ea5e9',
      warn: 'color: #f59e0b',
      error: 'color: #ef4444; font-weight: bold',
      debug: 'color: #64748b',
    };

    console.log(
      `%c[${entry.timestamp}] [${level.toUpperCase()}] ${message}`,
      styles[level],
      context || ''
    );

    // Send errors to Sentry
    if (level === 'error') {
      Sentry.captureMessage(message, {
        level: 'error',
        extra: context as Record<string, unknown>,
      });
    }
  }

  info(message: string, context?: unknown) {
    this.log('info', message, context);
  }
  warn(message: string, context?: unknown) {
    this.log('warn', message, context);
  }
  error(message: string, context?: unknown) {
    this.log('error', message, context);
  }
  debug(message: string, context?: unknown) {
    this.log('debug', message, context);
  }

  getHistory() {
    return [...this.history];
  }

  /**
   * Start a performance measurement
   */
  startTimer(name: string): void {
    this.performanceMetrics.set(name, {
      name,
      startTime: performance.now(),
    });
  }

  /**
   * End a performance measurement and return duration
   */
  endTimer(name: string): number {
    const metric = this.performanceMetrics.get(name);
    if (!metric) {
      this.warn(`Timer '${name}' not found`);
      return 0;
    }

    const duration = performance.now() - metric.startTime;
    metric.duration = duration;

    // Report to Sentry as a measurement
    Sentry.setMeasurement(name, duration, 'millisecond');

    this.debug(`Performance: ${name}`, { duration: `${duration.toFixed(2)}ms` });
    return duration;
  }

  /**
   * Track API latency
   */
  async trackApiCall<T>(name: string, fn: () => Promise<T>): Promise<T> {
    this.startTimer(name);
    try {
      const result = await fn();
      this.endTimer(name);
      return result;
    } catch (error) {
      this.endTimer(name);
      throw error;
    }
  }

  /**
   * Run System Diagnostics
   */
  async runDiagnostics(): Promise<SystemHealth> {
    const start = performance.now();

    // Check Storage
    let storageStatus: 'healthy' | 'warning' | 'error' = 'healthy';
    try {
      localStorage.setItem('__health_check__', '1');
      localStorage.removeItem('__health_check__');
    } catch {
      storageStatus = 'error';
    }

    // Check AI Engine via server-side proxy
    let aiStatus: 'ready' | 'missing-key' | 'unreachable' = 'missing-key';
    try {
      const { testConnectivity } = await import('./openaiService');
      const isConnected = await testConnectivity();
      aiStatus = isConnected ? 'ready' : 'unreachable';
    } catch {
      aiStatus = 'unreachable';
    }

    // Check Permissions
    let micStatus: 'granted' | 'denied' | 'prompt' = 'prompt';
    try {
      if (
        navigator.permissions &&
        (navigator.permissions as { query: (arg: { name: string }) => Promise<{ state: string }> })
          .query
      ) {
        const permission = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        micStatus = permission.state as 'granted' | 'denied' | 'prompt';
      }
    } catch {
      micStatus = 'denied';
    }

    return {
      storage: storageStatus,
      network: navigator.onLine ? 'online' : 'offline',
      aiEngine: aiStatus,
      permissions: micStatus,
      latency: Math.round(performance.now() - start),
    };
  }
}

export const logger = new Logger();
